// Top-level chat surface — replaces the deleted MessageList.tsx. assistant-ui's
// ThreadPrimitive owns scroll-follow/auto-scroll/virtualization; agenthub no
// longer hand-rolls scroll anchoring (the ~450-line MessageList.tsx this
// replaces had 3 separate hand-written scroll effects). Styled against the
// Claude-clone reference (assistant-ui.com/examples/claude): warm/serif
// aesthetic swapped for agenthub's Spectrum-token-backed Tailwind theme.
//
// Scroll behavior follows the active stream while the reader is near the
// bottom, pauses when they scroll upward, and resumes when they return near
// the bottom. assistant-ui owns that reader-aware state. An opened thread
// starts at the top (see useFollowStreamAfterHydration).
//
import { createContext, useContext, useEffect, useState, type FC } from 'react';
import { AuiIf, ThreadPrimitive, useAuiState } from '@assistant-ui/react';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { ChatMessage } from '@/entities/message/ui/ChatMessage';
import { SendComposer } from '@/features/chat/send-message/ui/SendComposer';
import { InterruptPanel, SpecialistQuestionBanner } from '@/features/chat/user-question';
import { ConnectProviderBanner } from '@/features/plugins/connect';
import { SelectionToolbar } from '@/shared/ui/quote';
import { ChatEmptyStateHeader, ChatEmptyStatePrompts } from './ChatEmptyState';
import { restoreThreadScrollTop, scrollToCurrentTurnStart } from './threadScroll';
import { useFollowStreamAfterHydration } from './useFollowStreamAfterHydration';
export interface ThreadProps {
  agentId: string;
  threadId?: string | null;
}

const ThreadAgentIdContext = createContext<string | undefined>(undefined);

function ThreadMessage() {
  return <ChatMessage agentId={useContext(ThreadAgentIdContext)} />;
}

const threadMessageComponents = { Message: ThreadMessage };

const DEFAULT_DISCLAIMER = 'This content is AI-generated and may contain errors. Please verify important information.';

// Per-agent overrides for the footer disclaimer. Most agents use the
// generic default; add an entry here only when an agent needs its own text.
const AGENT_DISCLAIMERS: Record<string, string> = {
  fieldy: 'This content is AI-generated and may contain errors. Please verify important information. For authorized use only. Do not share AI Agent results without permission. Treat all results as confidential. Customer Value Agent can run an anonymized version to run with the customer, if needed.',
};

function useThreadScrollPersistence(threadId: string | null | undefined) {
  const key = `agenthub:thread-scroll:${threadId ?? 'new'}`;
  useEffect(() => {
    const viewport = document.querySelector<HTMLElement>('.thread-viewport');
    if (!viewport) return undefined;
    const saved = window.sessionStorage.getItem(key);
    const restore = () => restoreThreadScrollTop(viewport, saved);
    const frame = window.requestAnimationFrame(restore);
    const onScroll = () => {
      window.sessionStorage.setItem(key, String(viewport.scrollTop));
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [key]);
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const viewport = document.querySelector<HTMLElement>('.thread-viewport');
    if (!viewport) return undefined;
    const onScroll = () => setVisible(viewport.scrollTop > 400);
    onScroll();
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <button
      type="button"
      aria-label="Scroll to top"
      data-testid="thread-scroll-to-top"
      onClick={() => {
        const viewport = document.querySelector<HTMLElement>('.thread-viewport');
        if (viewport) scrollToCurrentTurnStart(viewport);
      }}
      className={`sticky top-2 z-10 size-8 self-center rounded-full border border-border bg-background p-2 text-muted-foreground shadow-sm ${visible ? '' : 'invisible'}`}
    >
      <ArrowUpIcon className="size-4" />
    </button>
  );
}

export const Thread: FC<ThreadProps> = ({ agentId, threadId }) => {
  useThreadScrollPersistence(threadId);
  const hasMessages = useAuiState((s) => s.thread.messages.length > 0);
  const followStream = useFollowStreamAfterHydration(hasMessages);
  return (
    <ThreadPrimitive.Root className="flex h-full flex-col items-stretch bg-background text-foreground">
      <SelectionToolbar />
      {/* Best-available mitigation for a reported bug where clicking a
          specialist card mid-stream snapped the viewport to the top --
          not confirmed against a real browser (no browser automation
          available in this environment). While a card streams, content
          above/below the fold keeps resizing (banner, checklist, activity
          text); `overflow-anchor:none` disables the browser's native CSS
          scroll-anchoring on this container so only assistant-ui's own
          scroll-follow logic (useThreadViewportAutoScroll) governs scroll
          position here, removing one candidate source of the two fighting
          over it. Re-verify manually if the bug recurs. */}
      <ThreadPrimitive.Viewport
        autoScroll={followStream}
        turnAnchor="bottom"
        scrollToBottomOnRunStart
        scrollToBottomOnInitialize={false}
        scrollToBottomOnThreadSwitch={false}
        className="thread-viewport flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:none] [overflow-anchor:none] [&::-webkit-scrollbar]:hidden"
      >
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <div className="flex grow flex-col items-center px-4 py-8">
            <div className="m-auto flex w-full max-w-2xl flex-col items-stretch gap-5">
              <ChatEmptyStateHeader agentId={agentId} />
              <SendComposer agentId={agentId} threadId={threadId} />
              <ChatEmptyStatePrompts agentId={agentId} />
            </div>
          </div>
        </AuiIf>

        <AuiIf condition={(s) => !s.thread.isEmpty}>
          <div className="mx-auto flex w-full max-w-[59.5rem] flex-1 flex-col px-4 pt-8">
            <ScrollToTopButton />
            <div className="pb-8">
              <ThreadAgentIdContext.Provider value={agentId}>
                <ThreadPrimitive.Messages components={threadMessageComponents} />
              </ThreadAgentIdContext.Provider>
              <SpecialistQuestionBanner />
              <ConnectProviderBanner />
              <InterruptPanel />
            </div>
            <ThreadPrimitive.ViewportFooter className="sticky bottom-0 mt-auto w-full bg-linear-to-b from-transparent via-background/85 to-background pt-4 pb-2">
              <ThreadPrimitive.ScrollToBottom
                data-testid="thread-scroll-to-bottom"
                aria-label="Scroll to bottom"
                className="absolute -top-12 left-1/2 z-10 -translate-x-1/2 rounded-full border border-border bg-background p-2 text-muted-foreground shadow-sm disabled:invisible"
              >
                <ArrowDownIcon className="size-4" />
              </ThreadPrimitive.ScrollToBottom>
              <SendComposer agentId={agentId} threadId={threadId} />
              <p className="pt-2 text-center text-xs text-muted-foreground">{AGENT_DISCLAIMERS[agentId] ?? DEFAULT_DISCLAIMER}</p>
            </ThreadPrimitive.ViewportFooter>
          </div>
        </AuiIf>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};
